"use client";

import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { BlinkingDot, TypingText } from "@/components/ui/typing-text";
import { IMAGES } from "@/lib/constants/images";

export function MissionSection() {
	const sectionRef = useRef<HTMLElement>(null);
	const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

	return (
		<section
			ref={sectionRef}
			className="py-20 md:py-32 bg-white dark:bg-zinc-950 overflow-hidden"
		>
			<div className="container mx-auto px-4">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
					<motion.div
						initial={{ opacity: 0, x: -40 }}
						animate={isInView ? { opacity: 1, x: 0 } : {}}
						transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
						className="space-y-6 md:space-y-8"
					>
						<div className="space-y-4">
							<h2 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-foreground leading-tight">
								EAT BETTER.
								<br />
								<span className="text-primary">LIVE STRONGER.</span>
							</h2>

							<p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-lg">
								High-protein, chef-crafted meals. Frozen fresh. Ready in
								minutes. No prep. No junk. Just serious fuel.
							</p>
						</div>

						<div className="space-y-4 border-l-4 border-primary pl-5">
							<p className="text-base md:text-lg font-medium text-foreground">
								Trusted by CrossFit champs and busy go-getters alike.
							</p>
							<p className="text-base md:text-lg text-muted-foreground">
								Backed by a decade of culinary craft and nutritional precision.
							</p>
						</div>

						<p className="text-xl md:text-2xl font-bold italic text-foreground min-h-14 md:min-h-12">
							<TypingText
								text="This isn't meal prep! It's a movement"
								speed={0.05}
								delay={0.5}
								showCursor={false}
							/>
							<BlinkingDot delay={0.5 + 39 * 0.05} />
						</p>

						<Button
							size="lg"
							className="h-14 px-8 text-base font-bold rounded-full shadow-lg hover:shadow-xl transition-all mt-2"
							asChild
						>
							<Link href="/build-a-box">
								Start Your Box
								<ArrowRight className="ml-2 h-5 w-5" />
							</Link>
						</Button>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 40 }}
						animate={isInView ? { opacity: 1, x: 0 } : {}}
						transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
						className="relative"
					>
						<div className="relative aspect-square sm:aspect-4/3 lg:aspect-square rounded-3xl overflow-hidden shadow-2xl">
							<Image
								src={IMAGES.home.nickAndCrew}
								alt="Ice Age Meals - Chef-crafted high-protein meals"
								fill
								className="object-cover object-top"
								sizes="(max-width: 640px) 90vw, (max-width: 1024px) 50vw, 600px"
								quality={60}
							/>
							<div className="absolute inset-0 bg-linear-to-t from-black/20 to-transparent" />
						</div>
						<div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary/10 rounded-2xl -z-10 hidden lg:block" />
						<div className="absolute -top-4 -right-4 w-32 h-32 bg-primary/5 rounded-full -z-10 hidden lg:block" />
					</motion.div>
				</div>
			</div>
		</section>
	);
}
